import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faUserMd,
  faStethoscope,
  faStar,
  faCheckCircle,
  faCalendarCheck,
  faCalendarAlt,
  faUserCog,
} from "@fortawesome/free-solid-svg-icons";
import DoctorCard from "../../components/Patient/DoctorCard";
import Breadcrumb from "../../components/Breadcrumb";
import PatientPortalLayout from "../../components/Patient/PatientPortalLayout";
import styles from "../../components/Patient/PatientPortal.module.css";

const PatientDashboard = () => {
  const [doctors, setDoctors] = useState([]);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [patientName, setPatientName] = useState("Patient");
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const patientId = localStorage.getItem("userId");

    const fetchDoctors = async () => {
      try {
        const response = await axios.get("/api/doctor/all", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.doctors) {
          setDoctors(response.data.doctors.reverse());
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };

    const fetchPatientAppointments = async () => {
      try {
        if (patientId) {
          const response = await axios.get(
            `/api/appointment/patient/${patientId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setTotalAppointments(response.data.appointment?.length || 0);
        }
      } catch (error) {
        console.error("Error fetching appointments:", error);
      }
    };

    const fetchPatientProfile = async () => {
      try {
        if (patientId) {
          const response = await axios.get(`/api/patient/${patientId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.data.patient?.name) {
            setPatientName(response.data.patient.name);
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchDoctors();
    fetchPatientAppointments();
    fetchPatientProfile();
  }, [token]);

  return (
    <PatientPortalLayout
      activePage="home"
      patientName={patientName}
      totalAppointments={totalAppointments}
    >


      {/* ─── HERO WELCOME BANNER ─────────────────────────────────────── */}
      <section className={styles.heroBanner}>
        <div className={styles.heroBannerText}>
          <h1 className={styles.heroBannerTitle}>
            Welcome back, {patientName.split(" ")[0]}
          </h1>
          <p className={styles.heroBannerSub}>
            Your personalized care plan is active. Track recovery progress,
            manage upcoming therapy consultations, and connect directly with
            clinical specialists.
          </p>
        </div>

        <div className={styles.instantActionCard}>
          <span className={styles.actionCardStat}>{totalAppointments}</span>
          <span className={styles.actionCardLabel}>
            Active Care Consultations
          </span>
          <button
            type="button"
            onClick={() => navigate("/myappointment")}
            className={styles.bookBtnCTA}
          >
            <FontAwesomeIcon icon={faCalendarPlus} />
            <span>Manage Appointments</span>
          </button>
        </div>
      </section>

      {/* ─── METRIC STATS STRIP ──────────────────────────────────────── */}
      <div className={styles.statsStrip}>
        <div className={styles.metricCard}>
          <div className={styles.metricIconBox}>
            <FontAwesomeIcon icon={faCalendarCheck} />
          </div>
          <div className={styles.metricData}>
            <span className={styles.metricValue}>{totalAppointments}</span>
            <span className={styles.metricLabel}>
              Total Booked Consultations
            </span>
          </div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricIconBox}>
            <FontAwesomeIcon icon={faUserMd} />
          </div>
          <div className={styles.metricData}>
            <span className={styles.metricValue}>{doctors.length}</span>
            <span className={styles.metricLabel}>
              Available Clinical Specialists
            </span>
          </div>
        </div>

        <div className={styles.metricCard}>
          <div className={styles.metricIconBox}>
            <FontAwesomeIcon icon={faStethoscope} />
          </div>
          <div className={styles.metricData}>
            <span className={styles.metricValue}>100%</span>
            <span className={styles.metricLabel}>
              Verified Practo Credentialing
            </span>
          </div>
        </div>
      </div>

      {/* ─── ASSIGNED SPECIALISTS (DOCTORS GRID) ─────────────────────── */}
      <section>
        <h2 className={styles.sectionHeading}>
          <div className={styles.headingAccent} />
          <span>Clinical Specialists & Therapists</span>
        </h2>

        <div className={styles.doctorsGrid}>
          {doctors.map((doctor) => (
            <div className={styles.doctorCardWrap} key={doctor._id}>
              <DoctorCard doctor={doctor} />
            </div>
          ))}
        </div>
      </section>
    </PatientPortalLayout>
  );
};

export default PatientDashboard;
